/**
 * Reusable plan tag + locked-feature helpers.
 *
 * Usage:
 *   <PlanTag plan="amateur" />         // Premium 9,90€/mois
 *   <PlanTag plan="pro" />             // Pro 29€/mois
 *   <LockedRow locked>...</LockedRow>  // grays out contents on FREE tier
 *
 * The user's plan is fetched via useAuth(); a plan is "unlocked" when the
 * user has a matching subscription OR is an admin.
 */
import React from "react";
import { Lock, Crown, Building2 } from "lucide-react";

/** Small pill badge naming the plan required to unlock a feature. */
export function PlanTag({ plan = "amateur", size = "xs", className = "" }) {
  if (plan === "pro") {
    return (
      <span
        data-testid={`plan-tag-pro`}
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#1F3A5F] text-white font-bold tracking-wider ${
          size === "xs" ? "text-[9px]" : "text-[10px]"
        } ${className}`}
        title="Réservé au plan Pro"
      >
        <Building2 className="w-2.5 h-2.5" /> PRO
      </span>
    );
  }
  return (
    <span
      data-testid={`plan-tag-premium`}
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A] font-bold tracking-wider ${
        size === "xs" ? "text-[9px]" : "text-[10px]"
      } ${className}`}
      title="Réservé au plan Premium"
    >
      <Crown className="w-2.5 h-2.5" /> PREMIUM
    </span>
  );
}

/**
 * Small lock icon shown next to premium features when the user can't access
 * them. Purely visual — clicks still bubble up so the child can trigger the
 * paywall as usual.
 */
export function LockIcon({ className = "" }) {
  return <Lock className={`w-3 h-3 text-neutral-400 ${className}`} data-testid="locked-icon" />;
}

/** True when the user has access to the given plan feature. */
export function hasPlanAccess(user, plan = "amateur") {
  if (!user) return false;
  if (user.role === "admin") return true;
  if (plan === "amateur") return !!user.subscription_active;
  if (plan === "pro") return user.plan === "pro";
  return true;
}

/**
 * Wrap ANY nav item / card in <LockedRow locked>...</LockedRow>. When locked,
 * the block is desaturated + slightly transparent so users can visually scan
 * which parts of the app require an upgrade, but keeps them clickable so the
 * paywall/UpgradeNudge modal can fire and pitch the plan.
 */
export function LockedRow({ locked = false, children, className = "" }) {
  if (!locked) return <>{children}</>;
  return (
    <span
      className={`opacity-60 grayscale hover:opacity-80 transition ${className}`}
      data-testid="locked-row"
    >
      {children}
    </span>
  );
}
